import { memo, useMemo } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { Icon, Text } from 'react-native-paper'
import { FOCUS_DIM_OPACITY } from '@/constants/monitor'
import { useSemanticColors } from '@/constants/SemanticColors'
import { locationArrowBlue } from '@/images'
import { getRotationStyle } from '@/lib/movement-utils'
import { useMonitorStore } from '@/stores/monitor-store'
import type { UserMarkerData, UserMarkerVariant } from '@/types/mapData'
import { Callout, Marker } from '@/ui/MapView'

type MarkerInstance = InstanceType<typeof Marker>

interface Props {
    marker: UserMarkerData
    markerRef?: React.Ref<MarkerInstance>
    focusedUserId: string | null
}

interface Visual {
    size: number
    dashed: boolean
    filled: boolean
    glyph: string
    borderColorAccent: 'warning' | 'user'
    fgColorAccent: 'warning' | 'user' | 'white'
}

function getVisual(variant: UserMarkerVariant, icon: string): Visual {
    switch (variant) {
        case 'moving':
            return {
                size: 32,
                dashed: false,
                filled: true,
                glyph: icon,
                borderColorAccent: 'user',
                fgColorAccent: 'white',
            }
        case 'on_site':
            return {
                size: 34,
                dashed: false,
                filled: true,
                glyph: 'map-marker',
                borderColorAccent: 'user',
                fgColorAccent: 'white',
            }
        case 'idle':
            return {
                size: 34,
                dashed: true,
                filled: false,
                glyph: 'alert-circle',
                borderColorAccent: 'warning',
                fgColorAccent: 'warning',
            }
        case 'off':
            return {
                size: 20,
                dashed: false,
                filled: false,
                glyph: icon,
                borderColorAccent: 'user',
                fgColorAccent: 'user',
            }
    }
}

function resolveAccentColor(
    accent: 'warning' | 'user' | 'white',
    userColor: string,
    warningColor: string
): string {
    if (accent === 'white') return '#fff'
    if (accent === 'warning') return warningColor
    return userColor
}

function MarkerBody({
    visual,
    color,
    borderColor,
    fgColor,
    isMoving,
    rotationStyle,
}: {
    visual: Visual
    color: string
    borderColor: string
    fgColor: string
    isMoving: boolean
    rotationStyle: ReturnType<typeof getRotationStyle>
}) {
    return (
        <View
            style={[
                styles.marker,
                {
                    width: visual.size,
                    height: visual.size,
                    borderRadius: visual.size / 2,
                    backgroundColor: visual.filled ? color : 'transparent',
                    borderColor,
                    borderStyle: visual.dashed ? 'dashed' : 'solid',
                    borderWidth: isMoving ? 0 : 2.5,
                },
                isMoving ? rotationStyle : undefined,
            ]}
        >
            <Icon source={visual.glyph} size={visual.size * 0.6} color={fgColor} />
        </View>
    )
}

export const UserStatusMarker = memo(function UserStatusMarker({
    marker,
    markerRef,
    focusedUserId,
}: Props) {
    const mapHeading = useMonitorStore(state => state.mapHeading)
    const semantic = useSemanticColors()

    const rotationStyle = useMemo(
        () => getRotationStyle(marker.rotation - mapHeading, marker.shouldFlip),
        [marker.rotation, marker.shouldFlip, mapHeading]
    )

    const opacity =
        focusedUserId === null || marker.userId === focusedUserId ? 1 : FOCUS_DIM_OPACITY

    const visual = useMemo(
        () => getVisual(marker.variant, marker.icon),
        [marker.variant, marker.icon]
    )

    const isMoving = marker.variant === 'moving'
    const useAndroidImage = Platform.OS === 'android' && isMoving
    const color = marker.userColor
    const borderColor = resolveAccentColor(visual.borderColorAccent, color, semantic.warning)
    const fgColor = resolveAccentColor(visual.fgColorAccent, color, semantic.warning)

    return (
        <Marker
            ref={markerRef}
            coordinate={marker.coordinate}
            anchor={{ x: 0.5, y: 0.5 }}
            rotation={useAndroidImage ? marker.heading - 180 : undefined}
            flat
            image={useAndroidImage ? locationArrowBlue : undefined}
            tracksViewChanges={true}
            opacity={opacity}
        >
            {!useAndroidImage && (
                <MarkerBody
                    visual={visual}
                    color={color}
                    borderColor={borderColor}
                    fgColor={fgColor}
                    isMoving={isMoving}
                    rotationStyle={rotationStyle}
                />
            )}
            <Callout>
                <View style={styles.callout}>
                    <Text style={[styles.calloutLabel, { color: marker.userColor }]}>
                        {marker.userName}
                    </Text>
                    <Text style={styles.calloutBody}>{marker.description}</Text>
                </View>
            </Callout>
        </Marker>
    )
})

const styles = StyleSheet.create({
    marker: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    callout: {
        paddingHorizontal: 4,
        paddingVertical: 2,
        minWidth: 120,
    },
    calloutLabel: {
        fontWeight: '600',
        fontSize: 13,
    },
    calloutBody: {
        fontSize: 12,
        color: '#444',
    },
})
